import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";

/**
 * Cloudflare Turnstile CAPTCHA component
 * Supports managed, non-interactive, and invisible challenge modes.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  {
    siteKey,
    onSuccess,
    onError,
    onExpire,
    theme = "light",
    size = "normal",
    action = "login",
    appearance = "always",
  },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Store latest callbacks in refs to avoid re-rendering Turnstile on parent state changes
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  });

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (err) {
          console.warn("Turnstile reset error:", err);
        }
      }
    },
    remove: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.warn("Turnstile remove error:", err);
        }
        widgetIdRef.current = null;
      }
    },
    getWidgetId: () => widgetIdRef.current,
  }));

  useEffect(() => {
    if (!siteKey) return;

    let isMounted = true;

    const renderWidget = () => {
      if (!isMounted || !containerRef.current || !window.turnstile) return;

      // Clean up previous widget if already rendered
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {}
        widgetIdRef.current = null;
      }

      // Clear container DOM
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          action,
          appearance,
          callback: (token) => {
            if (isMounted && onSuccessRef.current) {
              onSuccessRef.current(token);
            }
          },
          "error-callback": (errorCode) => {
            if (isMounted && onErrorRef.current) {
              onErrorRef.current(errorCode);
            }
          },
          "expired-callback": () => {
            if (isMounted && onExpireRef.current) {
              onExpireRef.current();
            }
          },
        });
        widgetIdRef.current = id;
      } catch (err) {
        console.error("Turnstile render error:", err);
      }
    };

    // Ensure Turnstile script is loaded
    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector(
        'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
      );

      if (!script) {
        script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      const intervalId = setInterval(() => {
        if (window.turnstile) {
          clearInterval(intervalId);
          renderWidget();
        }
      }, 100);

      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
      }, 15000);

      return () => {
        isMounted = false;
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        if (widgetIdRef.current !== null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (e) {}
          widgetIdRef.current = null;
        }
      };
    }

    return () => {
      isMounted = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, size, action, appearance]);

  if (!siteKey) return null;

  return (
    <div
      ref={containerRef}
      className="cf-turnstile-container"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    />
  );
});

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onExpire: PropTypes.func,
  theme: PropTypes.oneOf(["light", "dark", "auto"]),
  size: PropTypes.oneOf(["normal", "compact", "flexible"]),
  action: PropTypes.string,
};

export default TurnstileWidget;
